import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import { Input, Select } from "@andorhealth/clientuireact.designsystem";
import {
  deleteVoiceAgentBinding,
  fetchLocationFilters,
  saveVoiceAgentBinding,
  type CatalogVoiceAgentBinding,
  type LocationFilterOption,
} from "@/lib/catalog-api";
import { useConfiguration } from "../hooks/useConfiguration";
import { useToast } from "../hooks/useToast";
import { saveErrorMessage } from "../lib/saveMessage";

type AgentScope = "device" | "tenant" | "org" | "site" | "location";

const GUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function bindingLabel(binding: CatalogVoiceAgentBinding): string {
  if (binding.deviceId) return `Device ${binding.deviceId}`;
  if (!binding.organizationId) return "Tenant default (all places)";
  const org = binding.organizationName || `Org ${binding.organizationId}`;
  if (binding.organizationSiteLocationId) {
    return `${org} / ${binding.siteName || "Site"} / ${binding.locationName || "Location"}`;
  }
  if (binding.organizationSiteId) {
    return `${org} / ${binding.siteName || "Site"}`;
  }
  return `${org} (org base)`;
}

export function VoiceStep() {
  const {
    voiceBindings,
    setVoiceBindings,
    noteSavedVoiceAgentId,
    setSaving,
    saving,
  } = useConfiguration();
  const toast = useToast();
  const [filterOrgs, setFilterOrgs] = useState<LocationFilterOption[]>([]);
  const [filterSites, setFilterSites] = useState<LocationFilterOption[]>([]);
  const [filterLocations, setFilterLocations] = useState<LocationFilterOption[]>(
    [],
  );
  const [bindingScope, setBindingScope] = useState<AgentScope>("tenant");
  const [bindingDeviceId, setBindingDeviceId] = useState("");
  const [bindingOrgId, setBindingOrgId] = useState<number | "">("");
  const [bindingSiteId, setBindingSiteId] = useState<number | "">("");
  const [bindingLocationId, setBindingLocationId] = useState<number | "">("");
  const [bindingAgentId, setBindingAgentId] = useState("");
  const [editingBindingId, setEditingBindingId] = useState<number | null>(null);

  useEffect(() => {
    void (async () => {
      const result = await fetchLocationFilters({
        organizationId: bindingOrgId === "" ? null : Number(bindingOrgId),
        organizationSiteId: bindingSiteId === "" ? null : Number(bindingSiteId),
      });
      if (!result.ok) return;
      setFilterOrgs(result.organizations);
      setFilterSites(result.sites);
      setFilterLocations(result.locations);
    })();
  }, [bindingOrgId, bindingSiteId]);

  const resetBindingForm = () => {
    setEditingBindingId(null);
    setBindingScope("tenant");
    setBindingDeviceId("");
    setBindingOrgId("");
    setBindingSiteId("");
    setBindingLocationId("");
    setBindingAgentId("");
  };

  const saveBindingForm = async () => {
    if (!bindingAgentId.trim()) {
      toast.error("Voice agent id is required");
      return;
    }
    if (bindingScope === "device") {
      const deviceId = bindingDeviceId.trim();
      if (!GUID_RE.test(deviceId)) {
        toast.error("DeviceId must be a GUID");
        return;
      }
    } else if (bindingScope !== "tenant" && bindingOrgId === "") {
      toast.error("Organization is required for org/site/location overrides");
      return;
    }
    if (
      (bindingScope === "site" || bindingScope === "location") &&
      bindingSiteId === ""
    ) {
      toast.error("Site is required for site or location overrides");
      return;
    }
    if (bindingScope === "location" && bindingLocationId === "") {
      toast.error("Location is required for a location override");
      return;
    }

    setSaving(true);
    try {
      const result = await saveVoiceAgentBinding(
        {
          deviceId: bindingScope === "device" ? bindingDeviceId.trim() : null,
          organizationId:
            bindingScope === "tenant" || bindingScope === "device"
              ? null
              : Number(bindingOrgId),
          organizationSiteId:
            bindingScope === "tenant" ||
            bindingScope === "device" ||
            bindingScope === "org"
              ? null
              : Number(bindingSiteId),
          organizationSiteLocationId:
            bindingScope === "location" ? Number(bindingLocationId) : null,
          voiceAgentId: bindingAgentId.trim(),
          isActive: true,
        },
        editingBindingId ?? undefined,
      );
      if (!result.ok) {
        toast.error(saveErrorMessage(result.message));
        return;
      }
      if (bindingScope === "tenant") {
        noteSavedVoiceAgentId(bindingAgentId.trim());
      }
      if (result.binding) {
        const saved = result.binding;
        setVoiceBindings((prev) => {
          const index = prev.findIndex(
            (item) => item.voiceAgentBindingId === saved.voiceAgentBindingId,
          );
          if (index < 0) return [...prev, saved];
          return prev.map((item, itemIndex) =>
            itemIndex === index ? saved : item,
          );
        });
      }
      resetBindingForm();
      toast.success("Voice assistant binding saved");
    } catch (error) {
      toast.error(saveErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const removeBinding = async (id: number) => {
    setSaving(true);
    try {
      const result = await deleteVoiceAgentBinding(id);
      if (!result.ok) {
        toast.error(saveErrorMessage(result.message || "Delete failed"));
        return;
      }
      setVoiceBindings((prev) =>
        prev.filter((item) => item.voiceAgentBindingId !== id),
      );
      if (editingBindingId === id) {
        resetBindingForm();
      }
      toast.success("Voice assistant binding deleted");
    } catch (error) {
      toast.error(saveErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Box sx={{ bgcolor: "#f8fafc", border: "1px solid #e4e7ec", p: 1.5 }}>
        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
          How agents are chosen
        </Typography>
        <Typography sx={{ color: "text.secondary", fontSize: "0.9rem" }}>
          Most specific wins: device → location → site → org → tenant default
          (everything empty). Assign an agent to a DeviceId when only that room
          should differ.
        </Typography>
      </Box>

      <Typography sx={{ fontWeight: 700, pt: 0.5 }}>Agent bindings</Typography>
      {voiceBindings.map((binding) => (
        <Box
          key={binding.voiceAgentBindingId}
          sx={{
            border: "1px solid #e4e7ec",
            p: 1.5,
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700 }}>{bindingLabel(binding)}</Typography>
            <Typography sx={{ color: "text.secondary", fontSize: "0.9rem" }}>
              Agent {binding.voiceAgentId}
              {!binding.isActive ? " · Inactive" : ""}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5}>
            <Button
              size="small"
              onClick={() => {
                setEditingBindingId(binding.voiceAgentBindingId);
                setBindingScope(
                  binding.deviceId
                    ? "device"
                    : !binding.organizationId
                      ? "tenant"
                      : binding.organizationSiteLocationId
                        ? "location"
                        : binding.organizationSiteId
                          ? "site"
                          : "org",
                );
                setBindingDeviceId(binding.deviceId ?? "");
                setBindingOrgId(binding.organizationId ?? "");
                setBindingSiteId(binding.organizationSiteId ?? "");
                setBindingLocationId(binding.organizationSiteLocationId ?? "");
                setBindingAgentId(binding.voiceAgentId);
              }}
            >
              Edit
            </Button>
            <IconButton
              aria-label="Delete binding"
              disabled={saving}
              onClick={() => void removeBinding(binding.voiceAgentBindingId)}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        </Box>
      ))}
      {!voiceBindings.length ? (
        <Typography sx={{ color: "text.secondary" }}>
          No bindings yet. Add a tenant default, a DeviceId, or an org/site/location
          override.
        </Typography>
      ) : null}

      <Divider />
      <Typography sx={{ fontWeight: 700 }}>
        {editingBindingId ? "Edit binding" : "Add binding"}
      </Typography>
      <Select
        label="Applies to"
        value={bindingScope}
        options={[
          { label: "Tenant default (all empty)", value: "tenant" },
          { label: "Device (DeviceId)", value: "device" },
          { label: "Organization", value: "org" },
          { label: "Site", value: "site" },
          { label: "Location", value: "location" },
        ]}
        onChange={(event) => {
          const next = event.target.value as AgentScope;
          setBindingScope(next);
          if (next === "tenant" || next === "device") {
            setBindingOrgId("");
            setBindingSiteId("");
            setBindingLocationId("");
          }
          if (next !== "device") setBindingDeviceId("");
          if (next === "org") {
            setBindingSiteId("");
            setBindingLocationId("");
          } else if (next === "site") {
            setBindingLocationId("");
          }
        }}
      />
      {bindingScope === "device" ? (
        <Input
          label="DeviceId"
          size="small"
          value={bindingDeviceId}
          onChange={(event) => setBindingDeviceId(event.target.value)}
          helperText="app.Device.DeviceId (GUID)"
        />
      ) : null}
      {bindingScope !== "tenant" && bindingScope !== "device" ? (
        <Select
          label="Organization"
          value={bindingOrgId}
          options={[
            { label: "Select…", value: "" },
            ...filterOrgs.map((org) => ({ label: org.name, value: org.id })),
          ]}
          onChange={(event) => {
            const raw = String(event.target.value);
            setBindingOrgId(raw === "" ? "" : Number(raw));
            setBindingSiteId("");
            setBindingLocationId("");
          }}
        />
      ) : null}
      {bindingScope === "site" || bindingScope === "location" ? (
        <Select
          label="Site"
          value={bindingSiteId}
          options={[
            { label: "Select…", value: "" },
            ...filterSites.map((site) => ({ label: site.name, value: site.id })),
          ]}
          onChange={(event) => {
            const raw = String(event.target.value);
            setBindingSiteId(raw === "" ? "" : Number(raw));
            setBindingLocationId("");
          }}
        />
      ) : null}
      {bindingScope === "location" ? (
        <Select
          label="Location"
          value={bindingLocationId}
          options={[
            { label: "Select…", value: "" },
            ...filterLocations.map((location) => ({
              label: location.name,
              value: location.id,
            })),
          ]}
          onChange={(event) => {
            const raw = String(event.target.value);
            setBindingLocationId(raw === "" ? "" : Number(raw));
          }}
        />
      ) : null}
      <Input
        label="Voice agent id"
        size="small"
        value={bindingAgentId}
        onChange={(event) => setBindingAgentId(event.target.value)}
        helperText="ElevenLabs agent id for this scope"
      />
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          disabled={saving}
          onClick={() => void saveBindingForm()}
        >
          {editingBindingId ? "Update" : "Add"}
        </Button>
        {editingBindingId ? (
          <Button onClick={resetBindingForm}>Cancel</Button>
        ) : null}
      </Stack>
    </Stack>
  );
}
